import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { urlTerminalAuthorizationService, urlCRMCompanyService } from "@src/endpoints"

const getParamString = (params) => {
  let string = ""
  if (Object.keys(params).length > 0) {
    Object.keys(params).map((key) => {
      if (!!params[key]) {
        string += `${key}=${params[key]}&`
      }
    })
  }
  return string
}

export const fetchAllTerminalAuthorizationData = createAsyncThunk(
  "TerminalAuthorization/fetchAllTerminalAuthorization",
  async (data, { rejectWithValue }) => {
    const { params, filters } = data
    try {
      const response = await axios.post(
        `${urlTerminalAuthorizationService}/GetAllTerminalAuthorization?${getParamString(data.params)}`, 
        data.filters || {}
      )
      return {
        params,
        filterData: filters,
        data: response.data.data || [],
        totalCount: response.data.totalCount || 1
      }
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue({
          status: error.response.status,
          messages: error.response.data.Messages
        })
      } else {
        return rejectWithValue({ Messages: ['An unexpected server error occurred.'] })
      }
    }
  }
)

export const getTerminalAuthorizationData = createAsyncThunk(
  "TerminalAuthorization/getTerminalAuthorizationData",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${urlTerminalAuthorizationService}/GetTerminalAuthorization/${id}`
      )
      return response.data
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue({
          status: error.response.status,
          messages: error.response.data.Messages
        })
      } else {
        return rejectWithValue({ Messages: ['An unexpected server error occurred.'] })
      }
    }
  }
)

export const addTerminalAuthorizationData = createAsyncThunk(
  "TerminalAuthorization/addTerminalAuthorizationData",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${urlTerminalAuthorizationService}/AddTerminalAuthorization`,
        data
      )
      return response.data
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue({
          status: error.response.status,
          messages: error.response.data.messages || error.response.data.Messages
        })
      } else {
        return rejectWithValue({ messages: ['An unexpected server error occurred.'] })
      }
    }
  }
)

export const updateTerminalAuthorizationData = createAsyncThunk(
  "TerminalAuthorization/updateTerminalAuthorizationData",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${urlTerminalAuthorizationService}/UpdateTerminalAuthorization`,
        data
      )
      return response.data
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue({
          status: error.response.status,
          messages: error.response.data.messages || error.response.data.Messages
        })
      } else {
        return rejectWithValue({ messages: ['An unexpected server error occurred.'] })
      }
    }
  }
)

export const addNoteTerminalAuthorization = createAsyncThunk(
  "TerminalAuthorization/addNoteTerminalAuthorization",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${urlTerminalAuthorizationService}/AddNoteTerminalAuthorization`,
        data
      )
      return response.data
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue({
          status: error.response.status,
          messages: error.response.data.Messages
        })
      } else {
        return rejectWithValue({ Messages: ['An unexpected server error occurred.'] })
      }
    }
  }
)

// Get active grain calendar (grain year + current week) for auto-population
export const getActiveGrainCalendar = createAsyncThunk(
  "TerminalAuthorization/getActiveGrainCalendar",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${urlTerminalAuthorizationService}/GetActiveGrainCalendar`
      )
      return response.data
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue({
          status: error.response.status,
          messages: error.response.data.Messages
        })
      } else {
        return rejectWithValue({ Messages: ['An unexpected server error occurred.'] })
      }
    }
  }
)

// Get the next sequence number for the Add form's read-only preview.
// in the active grain year, which is why it cannot be derived from the grid page.
export const getNextSequenceNumber = createAsyncThunk(
  "TerminalAuthorization/getNextSequenceNumber",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${urlTerminalAuthorizationService}/GetNextSequenceNumber`
      )
      return response.data
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue({
          status: error.response.status,
          messages: error.response.data.Messages
        })
      } else {
        return rejectWithValue({ Messages: ['An unexpected server error occurred.'] })
      }
    }
  }
)

// Get customers for dropdown
export const getCustomers = createAsyncThunk(
  "TerminalAuthorization/getCustomers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${urlCRMCompanyService}/GetAllCompanyRolesSelect/customer`
      )
      return response.data.map(el => ({
        value: el.companyCode,
        label: `${el.companyCode} - ${el.companyName}`,
        customerId: el.value
      }))
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue({
          status: error.response.status,
          messages: error.response.data.Messages
        })
      } else {
        return rejectWithValue({ Messages: ['An unexpected server error occurred.'] })
      }
    }
  }
)

export const terminalAuthorizationSlice = createSlice({
  name: "TerminalAuthorization",
  initialState: {
    terminalAuthorizationData: [], 
    totalCount: 0,
    loaded: true,
    loading: false,
    error: null,
    filter: {
      grainYear: "",
      transloadType: "",
      from: null,
      to: null,
      note: ""
    },
    params: {
      sort: "desc",
      // Newest created first (see table.js)
      sortColumn: "CreatedDate",
      q: '',
      page: 1,
      perPage: 10
    },
    filterForm: {} 
  },
  reducers: {
    setFilterForm: (state, action) => {       
      state.filterForm = action.payload      
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllTerminalAuthorizationData.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchAllTerminalAuthorizationData.fulfilled, (state, action) => {
        state.terminalAuthorizationData = action.payload.data || []
        state.params = action.payload.params
        state.totalCount = action.payload.totalCount
        state.loading = false
      })
      .addCase(fetchAllTerminalAuthorizationData.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
      .addCase(getTerminalAuthorizationData.pending, (state) => {
        state.loading = true
      })
      .addCase(getTerminalAuthorizationData.fulfilled, (state, action) => {
        state.loading = false
        state.terminalAuthorizationData = action.payload
      })
      .addCase(getTerminalAuthorizationData.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
      .addCase(addTerminalAuthorizationData.pending, (state) => {
        state.loading = true
      })
      .addCase(addTerminalAuthorizationData.fulfilled, (state, action) => {
        state.loading = false
        state.successMessage = action.payload
      })
      .addCase(addTerminalAuthorizationData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.messages || action.error?.message || 'An error occurred'
      })
      .addCase(updateTerminalAuthorizationData.pending, (state) => {
        state.loading = true
        state.error = null 
      })
      .addCase(updateTerminalAuthorizationData.fulfilled, (state, action) => {
        state.loading = false
        state.successMessage = action.payload.messages[0]
        state.error = null 
      })
      .addCase(updateTerminalAuthorizationData.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
        state.successMessage = null
      })
      .addCase(addNoteTerminalAuthorization.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addNoteTerminalAuthorization.fulfilled, (state, action) => {
        state.loading = false
        state.successMessage = action.payload.messages?.[0] || 'Note added successfully'
        state.error = null
      })
      .addCase(addNoteTerminalAuthorization.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.messages || ['Failed to add note']
        state.successMessage = null
      })
  }
})

export const { setFilterForm } = terminalAuthorizationSlice.actions

export default terminalAuthorizationSlice.reducer
